import React, { useMemo } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useStacklineTheme } from '@stackline/ui';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { useBaseMetricRequestBuilder } from 'src/components/BeaconV2Components/ExperimentalLayout/Forecasts/serverProxy/useBaseMetricRequestBuilder';
import { REVIEW_SENTIMENT_ENUM } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/constants';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { GenericXAxisLabel } from 'src/components/BeaconV2Components/TopEntityChartV2/GenericXAxisLabel';
import TopEntityChartV2 from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2';
import TopEntityChartV2Loading from 'src/components/BeaconV2Components/TopEntityChartV2/TopEntityChartV2Loading';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { useMainTimePeriod, useMainTimePeriodShortDisplayName } from 'src/utils/Hooks';
import useGenericAdvancedSearch from 'src/utils/Hooks/useGenericAdvancedSearch';
import { saveSingleBarChartAsDelimitedData } from 'src/utils/chartUtils';
import { METRICTYPE } from 'src/utils/entityDefinitions';

const useKeywordCountPositiveReviewData = (reviewType: 'positive' | 'negative') => {
  const { startWeek, endWeek } = useMainTimePeriod();

  const requestBuilder = useBaseMetricRequestBuilder({
    requestId: 'keywordCountByPositiveReviews',
    indexName: 'review-metrics',
    groupByFieldName: 'extractedKeywords.keyword',
    startWeekId: startWeek,
    endWeekId: endWeek,
    fields: [{ name: 'reviewSentiment' }]
  });

  requestBuilder.setPageSize(100);

  const reviewSentimentId = reviewType === 'positive' ? REVIEW_SENTIMENT_ENUM.POSITIVE : REVIEW_SENTIMENT_ENUM.NEGATIVE;

  requestBuilder.addConditionTermFilter('reviewSentiment', 'must', [reviewSentimentId]);

  requestBuilder.addSortField('reviewSentiment', 'reviewSentiment', 'value_count', false);

  const request = requestBuilder.build();
  const { data: response, isLoading } = useGenericAdvancedSearch({
    requestId: 'sentimentByTopic-stackedColumn',
    requestBody: [request],
    queryKeys: [request]
  });

  const data = useMemo(() => {
    if (response) {
      try {
        const rawData = response?.data?.[0]?.aggregations?.['by_extractedKeywords.keyword'] ?? [];
        const chartData = rawData.map((datum) => [datum.fieldId, datum.value]);

        return { chartData };
      } catch (e) {
        console.error(e);
        return null;
      }
    } else {
      return null;
    }
  }, [response]);

  return { data, isLoading };
};

const KeywordCountBySentimentTypeColumnChart = ({ reviewType }: { reviewType: 'positive' | 'negative' }) => {
  const theme = useStacklineTheme();
  const { data, isLoading } = useKeywordCountPositiveReviewData(reviewType);
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const { displayName: mainTimePeriodDisplayName } = useMainTimePeriod();

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  return (
    <BeaconChartWithLegend
      title={`Keyword Count in ${reviewType === 'positive' ? 'Positive' : 'Negative'} Reviews`}
      loading={isLoading}
      loadingComponent={<TopEntityChartV2Loading />}
      includeSummaryInfo={false}
      primaryLegendProps={{ displayName: mainTimePeriodDisplayName }}
      subtitle={
        <BeaconSubtitleContainer>
          <Text variant="body1">
            {`The chart displays the most frequently mentioned keywords in ${reviewType} reviews for ${entityPhrase} `}
            {taxonomyText} {`reviews during the ${mainTimePeriodShortDisplayName} period`}
            .
            <SubtitleFilters />
          </Text>
        </BeaconSubtitleContainer>
      }
      convertSeriesToDelimitedData={async () => {
        return saveSingleBarChartAsDelimitedData(data?.chartData ?? []);
      }}
    >
      <TopEntityChartV2
        seriesColors={[theme.colors.primary]}
        xAxisLabelTextStyles={{ lineHeight: 1.6 }}
        xAxisFormatter={(xAxisCategory) => {
          return ReactDOMServer.renderToStaticMarkup(
            <GenericXAxisLabel xAxisCategory={xAxisCategory} wrapperStyle={{ width: '50px' }} />
          );
        }}
        chartSeries={[
          {
            type: 'column',
            data: data?.chartData ?? [],
            metricType: METRICTYPE.VOLUME
          }
        ]}
      />
    </BeaconChartWithLegend>
  );
};

export default KeywordCountBySentimentTypeColumnChart;
